/**
 * Shared encode/decode between this app's page-local `{ q, filters }` shape and the URL QUERY
 * STRING `core.SavedView`/`core.StaffScreenState` actually store (filter contract v3 §16: "a view
 * produces a URL, never a second shape of the state"). Used by both OutreachPage and
 * TaxReturnsPage so the two pages' saved-view wiring can't drift on what a `q`/`filter.*` key
 * means.
 */

export interface QueryState {
  q?: string;
  filters: Record<string, string>;
}

const FILTER_PREFIX = 'filter.';

export function encodeQueryState(state: { q?: string; filters?: Record<string, string> }): string {
  const params = new URLSearchParams();
  if (state.q) params.set('q', state.q);
  if (state.filters) {
    for (const [key, value] of Object.entries(state.filters)) params.set(key, value);
  }
  return params.toString();
}

export function decodeQueryState(query: string): QueryState {
  const params = new URLSearchParams(query);
  const filters: Record<string, string> = {};
  let q: string | undefined;
  params.forEach((value, key) => {
    if (key === 'q') q = value;
    else if (key.startsWith(FILTER_PREFIX)) filters[key] = value;
  });
  return { q, filters };
}

/**
 * Splits a decoded filter set into the ones whose COLUMN still exists in `validColumnKeys`, and a
 * count of the ones that don't (a column renamed or removed since the view/default was saved).
 * Column-existence only -- not a full operator/value re-validation, which `applyClientFilters`'s
 * own forgiving-grammar skip already covers silently; this is specifically for the "visible
 * notice instead of silence" rule on OPENING a saved view (§16.2).
 */
export function splitStaleFilters(
  filters: Record<string, string>,
  validColumnKeys: ReadonlySet<string>
): { valid: Record<string, string>; staleCount: number } {
  const valid: Record<string, string> = {};
  let staleCount = 0;
  for (const [key, value] of Object.entries(filters)) {
    const withoutPrefix = key.slice(FILTER_PREFIX.length);
    const col = withoutPrefix.slice(0, withoutPrefix.lastIndexOf('.'));
    if (validColumnKeys.has(col)) valid[key] = value;
    else staleCount++;
  }
  return { valid, staleCount };
}
